import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from '../hooks/use-toast';
import { Mail, Globe, Shield, ArrowLeft, Save, Building, Loader2 } from 'lucide-react';
import api from '../api/axios';

const WebmasterConfig = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    smtp_host: '',
    smtp_port: '',
    smtp_user: '',
    smtp_password: '',
    smtp_from_name: '',
    smtp_from_email: '',
    restaurant_name: '',
    restaurant_address: '',
    restaurant_phone: '',
    restaurant_siret: '',
    restaurant_siren: '',
    restaurant_tva_intra: '',
    restaurant_capital: '',
    restaurant_forme: '',
    restaurant_ape: '',
    tva_rate: '',
  });

  useEffect(() => {
    const token = localStorage.getItem('staffToken');
    const role = localStorage.getItem('staffRole');
    if (!token) { navigate('/staff/login'); return; }
    if (role !== 'webmaster') { navigate('/staff/dashboard'); return; }
    fetchConfig();
  }, [navigate]);

  const fetchConfig = async () => {
    try {
      const res = await api.get('/webmaster-config');
      setConfig(prev => ({ ...prev, ...res.data }));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.entries(config).forEach(([k, v]) => {
        if (v && v.trim()) payload[k] = v.trim();
      });
      await api.put('/webmaster-config', payload);
      toast({ title: 'Configuration sauvegardee', description: 'Les parametres ont ete mis a jour avec succes' });
    } catch (e) {
      toast({ title: 'Erreur', description: 'Impossible de sauvegarder', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const u = (field) => (e) => setConfig({ ...config, [field]: e.target.value });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#f5f1ed] to-white flex items-center justify-center">
        <Loader2 className="animate-spin text-gray-400" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f5f1ed] to-white">
      <div className="bg-[#3d2817] text-white py-4 shadow-lg">
        <div className="max-w-4xl mx-auto px-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold" data-testid="webmaster-config-title">Configuration Webmaster</h1>
            <p className="text-xs text-gray-300 mt-0.5">Parametres systeme et integrations</p>
          </div>
          <Button onClick={() => navigate('/staff/dashboard')} size="sm" className="bg-red-600 hover:bg-red-700 text-xs">
            <ArrowLeft size={14} className="mr-1" /> Dashboard
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {/* Email Configuration */}
        <Card className="border shadow-sm">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="flex items-center gap-2 text-base"><Mail size={18} className="text-blue-600" /> Configuration Email (Hostinger)</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-3">
            <p className="text-xs text-gray-500 bg-blue-50 p-2 rounded">
              Parametres Hostinger : Host = smtp.hostinger.com, Port = 465 (SSL) ou 587 (TLS).
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Serveur SMTP</Label><Input value={config.smtp_host} onChange={u('smtp_host')} className="h-8 text-sm" data-testid="smtp-host" /></div>
              <div><Label className="text-xs">Port SMTP</Label><Input value={config.smtp_port} onChange={u('smtp_port')} className="h-8 text-sm" data-testid="smtp-port" /></div>
              <div><Label className="text-xs">Utilisateur SMTP</Label><Input value={config.smtp_user} onChange={u('smtp_user')} className="h-8 text-sm" data-testid="smtp-user" /></div>
              <div><Label className="text-xs">Mot de passe SMTP</Label><Input type="password" value={config.smtp_password} onChange={u('smtp_password')} placeholder="••••••••" className="h-8 text-sm" data-testid="smtp-password" /></div>
              <div><Label className="text-xs">Nom expediteur</Label><Input value={config.smtp_from_name} onChange={u('smtp_from_name')} className="h-8 text-sm" data-testid="smtp-from-name" /></div>
              <div><Label className="text-xs">Email expediteur</Label><Input value={config.smtp_from_email} onChange={u('smtp_from_email')} className="h-8 text-sm" data-testid="smtp-from-email" /></div>
            </div>
          </CardContent>
        </Card>

        {/* Restaurant Info */}
        <Card className="border shadow-sm">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="flex items-center gap-2 text-base"><Globe size={18} className="text-green-600" /> Informations Restaurant</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Nom du restaurant</Label><Input value={config.restaurant_name} onChange={u('restaurant_name')} className="h-8 text-sm" data-testid="restaurant-name" /></div>
              <div><Label className="text-xs">Telephone</Label><Input value={config.restaurant_phone} onChange={u('restaurant_phone')} className="h-8 text-sm" data-testid="restaurant-phone" /></div>
              <div className="col-span-2"><Label className="text-xs">Adresse</Label><Input value={config.restaurant_address} onChange={u('restaurant_address')} className="h-8 text-sm" data-testid="restaurant-address" /></div>
            </div>
          </CardContent>
        </Card>

        {/* Legal Info */}
        <Card className="border shadow-sm">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="flex items-center gap-2 text-base"><Building size={18} className="text-amber-600" /> Informations Legales</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Forme juridique</Label><Input value={config.restaurant_forme} onChange={u('restaurant_forme')} className="h-8 text-sm" data-testid="restaurant-forme" /></div>
              <div><Label className="text-xs">Capital social (EUR)</Label><Input value={config.restaurant_capital} onChange={u('restaurant_capital')} className="h-8 text-sm" data-testid="restaurant-capital" /></div>
              <div><Label className="text-xs">SIRET</Label><Input value={config.restaurant_siret} onChange={u('restaurant_siret')} className="h-8 text-sm" data-testid="restaurant-siret" /></div>
              <div><Label className="text-xs">SIREN</Label><Input value={config.restaurant_siren} onChange={u('restaurant_siren')} className="h-8 text-sm" data-testid="restaurant-siren" /></div>
              <div><Label className="text-xs">N TVA Intracommunautaire</Label><Input value={config.restaurant_tva_intra} onChange={u('restaurant_tva_intra')} className="h-8 text-sm" data-testid="restaurant-tva" /></div>
              <div><Label className="text-xs">Code APE/NAF</Label><Input value={config.restaurant_ape} onChange={u('restaurant_ape')} className="h-8 text-sm" data-testid="restaurant-ape" /></div>
              <div><Label className="text-xs">Taux TVA (%)</Label><Input value={config.tva_rate} onChange={u('tva_rate')} className="h-8 text-sm" data-testid="tva-rate" /></div>
            </div>
          </CardContent>
        </Card>

        {/* Security */}
        <Card className="border shadow-sm">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="flex items-center gap-2 text-base"><Shield size={18} className="text-purple-600" /> Securite</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <p className="text-xs text-gray-500 mb-2">Acces en temps reel aux utilisateurs et sessions actives.</p>
            <Button onClick={() => navigate('/staff/users')} variant="outline" size="sm" className="text-xs" data-testid="manage-users-btn">Gerer les utilisateurs</Button>
          </CardContent>
        </Card>

        <Button onClick={handleSave} disabled={saving} className="w-full bg-[#2c1810] hover:bg-[#4a2c1a] text-white h-10 font-semibold" data-testid="save-config-btn">
          {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />}
          {saving ? 'Sauvegarde en cours...' : 'Sauvegarder la configuration'}
        </Button>
      </div>
    </div>
  );
};

export default WebmasterConfig;
